<template>
    <div>
        <DCard padding>
            <DRow
                v-if="data.status !== 'open' && data.clientCustomerId === null && userPermissions.includes('customer.create')"
                class="mb-3"
                align-horizontal="start"
                align-vertical="end"
            >
                <DCol sm="3">
                    <DMultiSelect
                        id="customers-dropdown"
                        :label="__('orders.check.automatic_checks_tab.matching_customers')"
                        :options="customerOptions"
                        :multiple="false"
                        selection-type="text"
                        :selected="matchedCustomer"
                        @update="updateMatchedCustomer"
                    />
                </DCol>
                <DCol sm="2">
                    <DButton
                        id="find-customer"
                        icon="magnify"
                        :label="__('orders.check.automatic_checks_tab.find_customer')"
                        @click="showPsaCustomerFindDialog = true"
                    />
                </DCol>
            </DRow>
            <DTable
                id="automatic-check-table"
                :fields="fields"
                :items="form.checks"
            >
                <template #field="{item}">
                    <td>
                        {{ __('orders.check.automatic_checks_tab.fields.'+item.field) }}
                    </td>
                </template>
                <template #result="{item}">
                    <td>
                        <DIcon
                            v-if="item.result === true"
                            :id="`check_circle_${item.field}`"
                            name="check-circle"
                            :size="18"
                            class="color-green"
                        />
                        <DIcon
                            v-else
                            :id="`minus_circle_${item.field}`"
                            name="minus-circle"
                            :size="18"
                            class="color-red"
                        />
                    </td>
                </template>
                <template #update="{item}">
                    <td v-if="data.status === 'screening' && data.clientCustomerId !== null && item.result === false && item.field !== 'customer_id' && item.field !== 'iban' && userPermissions.includes('order.accept')">
                        <DFormCheck
                            :id="`update_${item.field}`"
                            :checked="item.update"
                            style="cursor: pointer"
                            @change="check(item.field)"
                        />
                    </td>
                    <td v-else />
                </template>
                <template #actions="{item}">
                    <td>
                        <DButton
                            v-if="data.status === 'screening' && data.clientCustomerId !== null && item.result === false && item.field !== 'customer_id' && userPermissions.includes('order.accept')"
                            :id="`edit_${item.field}`"
                            mini
                            icon="pencil"
                            @click="openReasonModal(item)"
                        />
                    </td>
                </template>
            </DTable>
            <OrderAutomaticCheckReason
                v-if="showOrderReasonModal"
                :item="selectedItem"
                :order="data"
                :show-modal="showOrderReasonModal"
                @close-modal="closeReasonModal"
            />
        </DCard>
        <DFabs>
            <CheckButtons>
                <template #additional_buttons>
                    <DFab
                        v-if="data.status === 'screening' && data.clientCustomerId !== null && userPermissions.includes('order.accept')"
                        id="btn-save"
                        :disabled="disableUpdateButton || isInLoadingState"
                        icon="content-save"
                        :label="__('orders.check.update_customer')"
                        :class="disableUpdateButton ? 'disabled-button' : ''"
                        @click="updateCustomer"
                    />
                    <DFab
                        v-if="data.status !== 'open' && data.clientCustomerId === null && userPermissions.includes('customer.create')"
                        id="btn-assign-customer"
                        icon="clipboard-account"
                        :label="__('orders.check.assign_customer')"
                        :disabled="matchedCustomerForInsert === null"
                        @click="assignCustomer"
                    />
                    <DFab
                        v-if="data.status !== 'open' && data.clientCustomerId === null && userPermissions.includes('customer.create')"
                        id="btn-create-customer"
                        icon="plus"
                        :label="__('orders.check.create_customer')"
                        @click="createCustomer"
                    />
                    <DFab
                        v-if="userPermissions.includes('order.accept')"
                        id="btn-assing"
                        :disabled="isInLoadingState"
                        icon="clipboard-account"
                        :label="__('orders.check.assign')"
                        @click="assign"
                    />
                </template>
            </CheckButtons>
        </DFabs>
        <PsaFindCustomerModal
            v-if="showPsaCustomerFindDialog"
            :show-modal="showPsaCustomerFindDialog"
            @confirm="confirmFindCustomer"
            @close="showPsaCustomerFindDialog = false"
        />
    </div>
</template>

<script>
import OrderBase from '~@/Pages/Orders/Psa/Checks/Base'
import App from '~@/App.vue'
import CheckButtons from '~@/Components/Orders/CheckButtons'
import OrderAutomaticCheckReason from '~@/Components/Orders/Psa/OrderAutomaticCheckReason'
import PsaFindCustomerModal from '~@/Components/Orders/Psa/PsaFindCustomerModal'
import { activeSepaMandateOf } from '~@/Composables/sepaMandate'

export default {
    name: 'AutomaticChecks',
    components: { PsaFindCustomerModal, OrderAutomaticCheckReason, CheckButtons },
    layout: [App, OrderBase],
    props: {
        data: {
            type: Object,
            default: () => { return {} }
        },
        orderAutomaticChecks: {
            type: Array,
            default: () => { return [] }
        }
    },
    data () {
        return {
            form: this.$inertia.form({
                checks: this.orderAutomaticChecks
            }),
            fields: [
                { key: 'field', label: '', sorter: false },
                { key: 'result', label: this.__('orders.check.automatic_checks_tab.result'), sorter: false },
                { key: 'weSellData', label: this.__('orders.check.automatic_checks_tab.wesell'), sorter: false },
                { key: 'update', label: this.__('orders.check.automatic_checks_tab.update'), sorter: false },
                { key: 'nitroData', label: this.__('orders.check.automatic_checks_tab.one_crm'), sorter: false },
                { key: 'reason', label: this.__('orders.check.automatic_checks_tab.reason_for_deviation'), sorter: false },
                { key: 'actions', label: '', sorter: false }
            ],
            selectedItem: null,
            showOrderReasonModal: false,
            disableUpdateButton: true,
            matchedCustomer: null,
            matchedCustomerForInsert: null,
            showPsaCustomerFindDialog: false
        }
    },
    computed: {
        userPermissions () {
            return this.$page.props.auth.permissions
        },
        customerOptions () {
            return this.data.databaseCustomers.map((item) => {
                return { value: item.id, text: item.company ? item.company + ' (' + item.id + ')' : item.firstname + ' ' + item.lastname + ' (' + item.id + ')', selected: String(item.id) === this.matchedCustomer }
            })
        }
    },
    methods: {
        updateCustomer () {
            this.$inertia.post(
                this.$route('orders.psa.update-customer', {
                    order: this.data.orderData.id
                })
                ,
                this.form,
                {
                    onSuccess: () => {
                        this.form.checks = this.orderAutomaticChecks
                        this.disableUpdateButton = true
                    }
                }
            )
        },
        assignCustomer () {
            this.$inertia.post(
                this.$route('orders.psa.assign_customer', {
                    order_id: this.data.nitroOrderId,
                    customer_id: this.matchedCustomerForInsert
                }),
                null,
                {
                    onSuccess: () => {
                        this.form.checks = this.orderAutomaticChecks
                    }
                }
            )
        },
        createCustomer () {
            this.$inertia.get(
                this.$route('customers.customers.create', {
                    order_no: this.data.orderData.id
                })
            )
        },
        check (field) {
            let disableUpdateButtonCheck = true

            this.form.checks.forEach((check, index) => {
                if (field === check.field) {
                    this.form.checks[index].update = !check.update
                }

                if (this.form.checks[index].update === true) {
                    disableUpdateButtonCheck = false
                }
            })

            this.disableUpdateButton = disableUpdateButtonCheck
        },
        openReasonModal (item) {
            this.selectedItem = item
            this.showOrderReasonModal = true
        },
        closeReasonModal () {
            this.form.checks = this.orderAutomaticChecks
            this.showOrderReasonModal = false
        },
        assign () {
            this.$inertia.post(
                this.$route('orders.psa.assign_user', {
                    order: this.data.orderNumber
                })
            )
        },
        updateMatchedCustomer (value) {
            this.matchedCustomer = value[0]
            let customerData = null

            this.data.databaseCustomers.forEach((customer) => {
                if (String(customer.id) === this.matchedCustomer) {
                    customerData = customer
                }
            })

            if (customerData === null) {
                return
            }

            this.matchedCustomerForInsert = this.matchedCustomer

            this.setCustomer(customerData)
        },
        confirmFindCustomer (customerData) {
            this.matchedCustomer = null
            if (customerData !== null) {
                this.matchedCustomerForInsert = customerData.id
                this.setCustomer(customerData)
            }

            this.showPsaCustomerFindDialog = false
        },
        setCustomer (customerData) {
            this.form.checks.forEach((check, index) => {
                if (check.field === 'herold_location_id') {
                    this.form.checks[index].nitroData = customerData.herold_location_id ?? ''
                }
                if (check.field === 'company') {
                    this.form.checks[index].nitroData = customerData.company ?? ''
                }
                if (check.field === 'customer_id') {
                    this.form.checks[index].nitroData = customerData.id ?? ''
                }
                if (check.field === 'city') {
                    this.form.checks[index].nitroData = customerData.city ?? ''
                }
                if (check.field === 'postal_code') {
                    this.form.checks[index].nitroData = customerData.postal_code ?? ''
                }
                if (check.field === 'address1') {
                    this.form.checks[index].nitroData = customerData.address1 ?? ''
                }
                if (check.field === 'phone') {
                    this.form.checks[index].nitroData = customerData.phone ?? ''
                }
                if (check.field === 'email') {
                    this.form.checks[index].nitroData = customerData.email ?? ''
                }
                if (check.field === 'vat_number') {
                    this.form.checks[index].nitroData = customerData.vat_number ?? ''
                }
                if (check.field === 'iban') {
                    this.form.checks[index].nitroData = this.getIban(customerData.sepa_mandates)
                }
                if (check.field === 'mobilenumber') {
                    this.form.checks[index].nitroData = customerData.mobilenumber ?? ''
                }
            })
        },
        getIban (sepaMandates) {
            return activeSepaMandateOf(sepaMandates)?.iban ?? ''
        }
    }
}
</script>

<style scoped>
.color-green {
    color: green;
}
.color-red {
    color: red;
}
.disabled-button {
    cursor: not-allowed !important;
}
</style>
